/**
 * ARStatusHistoryTab - Full audit trail of status changes on an architectural request.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, CircularProgress, Alert, Tooltip,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import api from "../../utils/api";

const fmtDT = (d) => d ? new Date(d).toLocaleString("en-US", {
  month:"short", day:"numeric", year:"numeric", hour:"2-digit", minute:"2-digit"
}) : "N/A";

const statusColor = (s) => {
  if (!s) return { bg:"rgba(0,0,0,0.06)", color:"#6b778c" };
  const sl = s.toLowerCase();
  if (sl.includes("approv"))  return { bg:"rgba(22,163,74,0.1)",   color:"#16a34a" };
  if (sl.includes("denied") || sl.includes("reject")) return { bg:"rgba(220,38,38,0.1)", color:"#dc2626" };
  if (sl.includes("pending")) return { bg:"rgba(180,83,9,0.1)",    color:"#b45309" };
  if (sl.includes("review"))  return { bg:"rgba(124,58,237,0.1)",  color:"#7c3aed" };
  if (sl.includes("complet")) return { bg:"rgba(3,105,161,0.1)",   color:"#0369a1" };
  return { bg:"rgba(47,99,173,0.1)", color:"#2f63ad" };
};

const ARStatusHistoryTab = ({ requestId }) => {
  const [history,  setHistory]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/architectural-requests/${requestId}/status-history`);
      setHistory(res.data || []);
    } catch {
      // Fallback: extract from timeline
      try {
        const tRes = await api.get(`/architecturalRequestTimeline/${requestId}`);
        const tl = tRes.data?.timeline || [];
        setHistory(tl.filter(e => e.type === "status_change" || (e.old_status && e.new_status)));
      } catch {
        setError("Failed to load status history.");
      }
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:2.5 }}>
        <HistoryIcon sx={{ color:"#2f63ad" }} />
        <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142" }}>Status Change History</Typography>
        <Chip size="small" label={`${history.length} changes`}
          sx={{ ml:1, fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor:"rgba(47,99,173,0.08)", color:"#2f63ad" }} />
      </Box>

      {error && <Alert severity="error" sx={{ mb:2, borderRadius:2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display:"flex", justifyContent:"center", py:6 }}><CircularProgress /></Box>
      ) : history.length === 0 ? (
        <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", p:4, textAlign:"center" }}>
          <HistoryIcon sx={{ fontSize:40, color:"rgba(47,99,173,0.2)", mb:1 }} />
          <Typography variant="body2" sx={{ color:"#6b778c" }}>No status changes recorded yet.</Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
          <Table size="small">
            <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}>
              <TableRow>
                {["Date & Time","Changed By","From","To","Notes"].map(h => (
                  <TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((h, i) => {
                const from    = h.old_status || h.from_status || h.previous_status || "-";
                const to      = h.new_status || h.to_status   || h.status          || "-";
                const by      = h.changed_by_name || h.changed_by || h.user_name   || "System";
                const at      = h.changed_at || h.created_at  || h.timestamp;
                const notes   = h.notes || h.comment || h.reason || "";
                const fromC   = statusColor(from !== "-" ? from : null);
                const toC     = statusColor(to   !== "-" ? to   : null);
                return (
                  <TableRow key={i} hover sx={{ "&:last-child td":{ border:0 } }}>
                    <TableCell sx={{ fontSize:12, color:"#6b778c", whiteSpace:"nowrap" }}>{fmtDT(at)}</TableCell>
                    <TableCell sx={{ fontWeight:600, color:"#223142", fontSize:12 }}>{by}</TableCell>
                    <TableCell>
                      {from !== "-" ? (
                        <Chip size="small" label={from} sx={{ fontWeight:600, fontSize:11, borderRadius:"8px", bgcolor:fromC.bg, color:fromC.color }} />
                      ) : <Typography variant="caption" sx={{ color:"#9aa5b4" }}>-</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={to} sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", bgcolor:toC.bg, color:toC.color }} />
                    </TableCell>
                    <TableCell sx={{ fontSize:11, color:"#6b778c", maxWidth:200 }}>
                      {notes ? (
                        <Tooltip title={notes}><span style={{ cursor:"help" }}>{notes.length>60 ? notes.slice(0,60)+"…" : notes}</span></Tooltip>
                      ) : "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default ARStatusHistoryTab;
